import * as React from 'react';
import { useLatestRequest } from '@/hooks/use-latest-request';
import { apiFetch } from '@/lib/api';
import type { QuickSearchResponse, QuickSearchResult } from '@/components/quick-search/quick-search-types';
import { QUICK_SEARCH_MIN_QUERY_LENGTH } from '../../../shared/search/quick-search.types';

const DEBOUNCE_MS = 180;

export type QuickSearchServerState = {
  query: string;
  results: QuickSearchResult[];
  loading: boolean;
  error: boolean;
  truncated: boolean;
};

const idleState: QuickSearchServerState = {
  query: '',
  results: [],
  loading: false,
  error: false,
  truncated: false,
};

export function useQuickSearchServer(query: string, enabled: boolean): QuickSearchServerState {
  const [state, setState] = React.useState<QuickSearchServerState>(idleState);

  const { startRequest } = useLatestRequest(JSON.stringify([query, enabled]));

  React.useEffect(() => {
    const isLatest = startRequest();
    if (!enabled) {
      setState(idleState);
      return;
    }

    const trimmed = query.trim();
    if (trimmed.length < QUICK_SEARCH_MIN_QUERY_LENGTH) {
      setState(idleState);
      return;
    }

    setState({ ...idleState, query, loading: true });

    const handle = window.setTimeout(() => {
      void (async () => {
        try {
          const params = new URLSearchParams({ q: trimmed });
          const response = await apiFetch<QuickSearchResponse>(`/api/quick-search?${params.toString()}`);
          if (!isLatest()) return;
          setState({
            query,
            results: response.results ?? [],
            loading: false,
            error: false,
            truncated: Boolean(response.truncated),
          });
        } catch {
          if (!isLatest()) return;
          setState({
            query,
            results: [],
            loading: false,
            error: true,
            truncated: false,
          });
        }
      })();
    }, DEBOUNCE_MS);

    return () => {
      window.clearTimeout(handle);
    };
  }, [enabled, query, startRequest]);

  if (!enabled || query.trim().length < QUICK_SEARCH_MIN_QUERY_LENGTH) return idleState;
  return state.query === query ? state : { ...idleState, query, loading: true };
}
